import type { ShallowResponse } from 'types/responses'
import type { Nullable } from 'types/helpers'

import type { PricingPlan } from 'types/pricing-plans'
import type { PayerData } from 'types/payer-data'
import type { ApiClientV2 } from 'utils/api-client/api-client.utils'
import type { DealPaymentMethodChoice } from 'shop/types/deal-payment-method-choice'

export interface DealDocumentLink {
  id: number
  title: string
  filename: string
  downloadUrl: string
}

interface SalesTeamMember {
  id: number
  avatar: string
  email: string
  fullName: string
  termsAccepted: number
  userProfile: any
  phone: string
}

export interface Deal {
  id: number
  commissionRate: string
  couponId: Nullable<number>
  incentivePlanId: number
  orderId: Nullable<number>
  payerData: PayerData
  payerEmail: string
  payerFullName: string
  payerId: Nullable<string>
  price: string
  pricingPlanId: number
  pricingPlan: PricingPlan
  productId: number
  product: any
  productSlug: string
  productIncentiveId: number
  productName: string
  salesTeamId: number
  salesTeamMember: SalesTeamMember
  salesTeamMemberId: number
  selectedPayMethod: string
  sellerId: number
  state: string
  token: Nullable<string>
  ownerData: any
  showCustomFields: boolean
  showContactInfo: boolean
  optInAnswers: any[]
  coupon: any
  idForSeller: number
  paymentLink: string
  previewLink: string
  orderToken?: string
  canceledAt?: Nullable<string>
  canceledByName?: Nullable<string>
  customConditions?: string
  customConditionsEnabled?: boolean
  showCustomConditionsInInvoice?: boolean
  /** SCL-1894 contract v1 — read-only, present on both deal surfaces. */
  paymentMethodChoice?: DealPaymentMethodChoice
  /** SCL-1894 R4 — the method actually used, null until something succeeds. */
  paidPayMethod?: Nullable<string>
  documents: DealDocumentLink[]
  documentsEditable: boolean
}

const BASE_URL = (username) => `/shop/${username}/deals`

export const createDealsApi = ({ GET, POST }: ApiClientV2) => ({
  fetchItem: (id: string, data: { username: string }) => GET(`${BASE_URL(data.username)}/${id}`, data),
  createOrder: (username, data) =>
    POST<ShallowResponse<{ redirectLink: string; clientSecret: string }>>(`${BASE_URL(username)}`, data),
  acceptDeal: (username, { id, ...data }) => POST<ShallowResponse<Deal>>(`${BASE_URL(username)}/${id}/accept`, data),
})

export type DealsApi = ReturnType<typeof createDealsApi>
